import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSearchContext } from '../../contexts/SearchContext';
import { useFacets } from '../../contexts/FacetsContext';
import { extractFacetsFromSearchResults, mergeRegionTagsRemoteCounts } from '../../utils/facets/facets-shared';

/**
 * Extracts facets from search results and updates FacetsContext.
 * Skips overwriting when we are still showing SSR/initial results so that
 * desktop category pills stay 100% aligned with the server-rendered static shell.
 */
export const FacetsUpdater = () => {
  const { results, initialResults, filters, remoteRegionTagsFacet } = useSearchContext();
  const { setFacetsData } = useFacets();
  const intl = useIntl();
  const locale = intl.locale;

  useEffect(() => {
    if (!results?.facets) return;
    // Keep server-passed facets until user triggers a new search (different results)
    if (initialResults != null && results === initialResults) {
      return;
    }
    const filtersObj =
      typeof filters === 'object' && filters ? (filters as Record<string, string[]>) : {};
    const facetsDataWithoutRemoteCounts = extractFacetsFromSearchResults(results, filtersObj, locale as 'en' | 'fi' | 'sv');
    // TEC-482 follow-up: without this, LocationHierarchyPicker's remote-job badges only reflected
    // whatever pages/index/+data.ts computed at the initial SSR pageload and never updated again
    // when a filter change re-ran the search client-side (see SearchContext.tsx's companion
    // remoteRegionTagsFacet query, refetched alongside `results` on every client search).
    const facetsData = mergeRegionTagsRemoteCounts(facetsDataWithoutRemoteCounts, remoteRegionTagsFacet);
    setFacetsData(facetsData);
  }, [results, initialResults, filters, remoteRegionTagsFacet, setFacetsData, locale]);

  return null;
};
